import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Award, Shield, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/hooks/useAuth';

export const AboutPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const handleStartAnalysis = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      navigate('/auth');
    }
  };
  
  return (
    <>
      <Helmet>
        <title>À propos de DPEWizard - Notre mission</title>
        <meta name="description" content="Découvrez la mission de DPEWizard : rendre les diagnostics énergétiques plus fiables grâce à l'intelligence artificielle et l'expertise technique." />
        <link rel="canonical" href="https://www.dpewizard.com/about" />
      </Helmet>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <BarChart3 className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-6">À propos de DPEWizard</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Notre mission est de rendre les diagnostics énergétiques plus fiables et accessibles. 
            Nous analysons votre DPE avec intelligence artificielle et expertise technique pour détecter 
            les erreurs et vous proposer des recommandations personnalisées.
          </p>
        </div>

        {/* Notre Mission */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Pourquoi DPEWizard ?</h2>
          <div className="prose prose-lg max-w-none text-gray-600 space-y-6">
            <p>
              Les diagnostics de performance énergétique (DPE) sont souvent entachés d'erreurs qui peuvent 
              avoir des conséquences importantes sur la valeur des biens immobiliers et les décisions de rénovation.
            </p>
            <p>
              DPEWizard combine intelligence artificielle et expertise technique pour analyser votre DPE, 
              détecter les incohérences et vous proposer des recommandations personnalisées d'amélioration énergétique.
            </p>
            <p>
              Notre plateforme s'appuie sur les données officielles de l'ADEME et applique 28 règles expertes 
              pour vous garantir une analyse précise et des conseils adaptés à votre situation.
            </p>
          </div>
        </div>

        {/* Nos Valeurs */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-12 text-center">Ce qui nous guide</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Précision",
                description: "Analyses fiables basées sur les données officielles ADEME et des règles expertes validées par des professionnels.",
                icon: <Award className="w-8 h-8 text-yellow-500" />
              },
              {
                title: "Transparence",
                description: "Explications claires de nos méthodes d'analyse et recommandations détaillées sans jargon technique.",
                icon: <Shield className="w-8 h-8 text-blue-500" />
              },
              {
                title: "Personnalisation",
                description: "Conseils adaptés à votre situation, vos contraintes et vos objectifs d'amélioration énergétique.",
                icon: <BookOpen className="w-8 h-8 text-green-500" />
              }
            ].map((value, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  {value.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8 md:p-12 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Optimisez votre DPE dès maintenant
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
            Analysez votre diagnostic énergétique, détectez les erreurs et obtenez des recommandations 
            personnalisées pour améliorer votre classe énergétique.
          </p>
          <Button
            variant="primary"
            size="xl"
            onClick={handleStartAnalysis}
            className="px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            Démarrer l'analyse de votre DPE
          </Button>
        </div>
      </div>
    </>
  );
};

export default AboutPage;